(function (root, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.AlvauraFulfillmentDisplay = api;
  if (!root?.customElements || root.customElements.get('alvaura-fulfillment-copy')) return;

  // Presentation only. The existing availability controller still owns the
  // Variant, ATC, sticky picker and Fulfillment line-item property.
  root.customElements.define('alvaura-fulfillment-copy', class extends root.HTMLElement {
    connectedCallback() {
      this.availability = this.closest('alvaura-availability');
      this.label = this.querySelector('strong');
      this.range = this.querySelector('[data-timing-range]');
      this.warning = this.querySelector('[data-editor-timing-warning]');
      if (!this.availability || !this.label || !this.range) return;
      this.originalLabel = this.label.textContent;
      try {
        this.settings = JSON.parse(this.availability.querySelector('[data-fulfillment-display-settings]')?.textContent || '{}');
      } catch { this.settings = {}; }
      this.refresh = () => {
        const isCurrent = this.availability.hasAttribute('data-ready') &&
          this.availability.dataset.state === this.dataset.copyState;
        const result = isCurrent ? api.presentation(new Date(), this.dataset.copyState, this.settings) : null;
        const label = result?.label || this.originalLabel;
        const range = result?.range || '';
        // Avoid unnecessary subtree changes observed by ELLA's form reconciler.
        if (this.label.textContent !== label) this.label.textContent = label;
        if (this.range.textContent !== range) this.range.textContent = range;
        this.range.hidden = !range;
        if (this.warning) {
          const warning = result?.warning || '';
          if (this.warning.textContent !== warning) this.warning.textContent = warning;
          this.warning.hidden = !warning;
        }
      };
      this.observer = new root.MutationObserver(this.refresh);
      this.observer.observe(this.availability, { attributes: true, attributeFilter: ['data-state', 'data-ready'] });
      this.visibleRefresh = () => { if (!root.document.hidden) this.refresh(); };
      root.document.addEventListener('visibilitychange', this.visibleRefresh);
      this.timer = root.setInterval(this.visibleRefresh, 60000);
      this.refresh();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      root.document.removeEventListener('visibilitychange', this.visibleRefresh);
      root.clearInterval(this.timer);
    }
  });
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  const isoDate = (value) => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    !Number.isNaN(Date.parse(value + 'T12:00:00Z')) && new Date(value + 'T12:00:00Z').toISOString().slice(0, 10) === value;
  const days = (value) => value !== '' && value != null && Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 90 ? Number(value) : null;
  const range = (min, max) => {
    const low = days(min), high = days(max);
    return low !== null && high !== null && low <= high ? [low, high] : null;
  };

  function calendar(now, settings) {
    const start = String(settings.timing_calendar_start || '').trim();
    const end = String(settings.timing_calendar_end || '').trim();
    if ((start && !isoDate(start)) || (end && !isoDate(end)) || (start && end && start > end) || !settings.timing_timezone) return null;
    let today;
    try {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: settings.timing_timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
      const part = (name) => parts.find((item) => item.type === name)?.value;
      today = `${part('year')}-${part('month')}-${part('day')}`;
    } catch { return null; }
    if (!isoDate(today) || (start && today < start) || (end && today > end)) return null;
    const excluded = String(settings.timing_holidays || '').split(/[\s,]+/).filter(Boolean);
    if (excluded.some((date) => !isoDate(date))) return null;
    return { today, end, excluded: new Set(excluded) };
  }

  function addBusinessDays(from, count, config) {
    const date = new Date(from + 'T12:00:00Z');
    let remaining = count;
    for (let attempts = 0; attempts < 366 && remaining > 0; attempts++) {
      date.setUTCDate(date.getUTCDate() + 1);
      const iso = date.toISOString().slice(0, 10);
      if (config.end && iso > config.end) return null;
      if (![0, 6].includes(date.getUTCDay()) && !config.excluded.has(iso)) remaining--;
    }
    return remaining === 0 ? date.toISOString().slice(0, 10) : null;
  }
  const format = (iso) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(new Date(iso + 'T12:00:00Z'));
  const numberRange = (values) => values[0] === values[1] ? String(values[0]) : values.join('–');

  function presentation(now, state, settings) {
    if (!['in_stock', 'made_to_order'].includes(state)) return null;
    const prefix = state === 'in_stock' ? 'stock' : 'mto';
    const custom = settings[`${prefix}_customize`] === true;
    const processing = range(settings[`${prefix}_processing_min`], settings[`${prefix}_processing_max`]);
    const transit = custom ? range(settings[`${prefix}_transit_min`], settings[`${prefix}_transit_max`]) : range(settings.transit_min, settings.transit_max);
    const mode = custom ? settings[`${prefix}_timing_mode`] || 'status' : settings.timing_mode || 'status';
    const showProcessing = custom ? settings[`${prefix}_show_processing`] === true : settings.show_processing_range === true;
    const showTransit = custom ? settings[`${prefix}_show_transit`] === true : settings.show_transit_range === true;
    const statusLabel = state === 'in_stock' ? settings.in_stock_label || 'Ready to ship' : settings.made_to_order_label || 'Made to order';
    const dispatchPrefix = custom ? settings[`${prefix}_dispatch_prefix`] || 'Ships by' : settings.dispatch_prefix || 'Ships by';
    const arrivalPrefix = custom ? settings[`${prefix}_arrival_prefix`] || 'Estimated delivery' : settings.arrival_prefix || 'Estimated arrival';
    const unit = String(settings.business_days_label || 'business days');
    const parts = [];
    if (showProcessing && processing) parts.push(`${settings.processing_prefix || 'Processing'} ${numberRange(processing)} ${unit}`);
    if (showTransit && transit) parts.push(`${settings.transit_prefix || 'Shipping after dispatch'} ${numberRange(transit)} ${unit}`);
    let label = null;
    let warning = '';
    const wantsDispatch = ['dispatch', 'label_dispatch', 'label_both'].includes(mode);
    const wantsArrival = ['arrival', 'label_arrival', 'label_both'].includes(mode);
    const config = calendar(now, settings);
    if (wantsDispatch || wantsArrival) {
      if (!processing) warning = 'Date hidden: enter processing minimum and maximum as whole working days from 1 to 90, with minimum no greater than maximum.';
      else if (wantsArrival && !transit) warning = 'Date hidden: enter shipping minimum and maximum as whole working days from 1 to 90, with minimum no greater than maximum.';
      else if (!config) warning = 'Date hidden: check the time zone, optional calendar bounds and non-working dates (YYYY-MM-DD).';
    }
    if (!warning && config && processing && (wantsDispatch || wantsArrival)) {
      const firstDispatch = addBusinessDays(config.today, processing[0], config);
      const lastDispatch = addBusinessDays(config.today, processing[1], config);
      const firstArrival = wantsArrival && firstDispatch && addBusinessDays(firstDispatch, transit[0], config);
      const lastArrival = wantsArrival && lastDispatch && addBusinessDays(lastDispatch, transit[1], config);
      if (!lastDispatch || (wantsArrival && (!firstArrival || !lastArrival))) {
        warning = 'Date hidden: the calculated date is outside the optional calendar end. Extend the reviewed calendar or clear that limit.';
      } else {
        const dateParts = [];
        if (mode.startsWith('label_')) dateParts.push(statusLabel);
        if (wantsDispatch) dateParts.push(`${dispatchPrefix} ${format(lastDispatch)}`);
        if (wantsArrival) dateParts.push(`${arrivalPrefix} ${format(firstArrival)}${firstArrival === lastArrival ? '' : `–${format(lastArrival)}`}`);
        label = dateParts.join(' · ');
      }
    }
    if (label && !custom && state === 'made_to_order') label = `${statusLabel} · ${label}`;
    if (!warning && ((showProcessing && !processing) || (showTransit && !transit))) warning = 'Time range hidden: check the enabled minimum and maximum working-day inputs.';
    return { label, range: parts.length ? ` · ${parts.join(' · ')}` : '', warning };
  }
  return { calendar, addBusinessDays, presentation };
});
